import type { MenuProps } from 'antd';
import { Layout, Menu } from 'antd';
import type React from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { NotificationStatusIndicator } from '@/components/NotificationStatusIndicator';
import { useAuth } from '@/core/providers/AuthFirebaseProvider';
import { useFCM } from '@/core/providers/FCMProvider';
import { Roles } from '@/core/utils/roles';
import dayjs from '../utils/dayjs-instance';
import { CountryHolidaysPageView } from './country-holydays/CountryHolidaysPageView';
import { DistrictsPageView } from './districts/DistrictsPageView';
import { DistrictsPageViewNew } from './districts-new/DistrictsPageViewNew';
import { HeaderSelect } from './HeaderSelect';
import LogoutButtonView from './LogoutButtonView';
import { ExpressOrdersProvider } from './orders/context/ExpressOrdersProvider';
import { OrderPageViewNew } from './orders/OrderPageView';
import { RealtimeNotify } from './RealtimeNotify';
import { TrackingPageView } from './tracking/TrackingPageView';
import { UTCOffsets } from './UTCOffsets';
import { UserEditPage } from './user/UserEditPage';

const ERP_BASE_URL = import.meta.env.VITE_ERP_BASE_URL;

const useUserRoles = () => {
  const { userDataParams } = useAuth();

  return {
    isManager:
      userDataParams?.roles.includes(Roles.manager) ||
      userDataParams?.roles.includes(Roles.seniorManager) ||
      false,
    isSeniorOperator: userDataParams?.roles.includes(Roles.seniorOperator) ?? false,
    isObserver: userDataParams?.roles.includes(Roles.observer) ?? false,
  };
};

const NavigationMenu: React.FC<{
  isObserver: boolean;
  isManager: boolean;
  isSeniorOperator: boolean;
}> = ({ isObserver, isManager, isSeniorOperator }) => {
  const isChile = false; // TODO: implement proper country detection

  const menuItems: MenuProps['items'] = [
    {
      key: '/',
      label: isObserver ? <Link to="/">Tracking</Link> : <Link to="/">Orders</Link>,
    },
    !isObserver && {
      key: '/tracking',
      label: <Link to="/tracking">Tracking</Link>,
    },
    !isObserver && {
      key: '/tracking-v2',
      label: (
        <a
          href={`${ERP_BASE_URL}/delivery/tracking/track/${dayjs().format('YYYY-MM-DD')}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Tracking v2
        </a>
      ),
    },
    isManager && {
      key: '/users',
      label: <Link to="/users">Users</Link>,
    },
    (isManager || isSeniorOperator) &&
      !isChile && {
        key: '/districts',
        label: <Link to="/districts">Districts</Link>,
      },
    (isManager || isSeniorOperator) && {
      key: '/districts2',
      label: <Link to="/districts2">Districts v2</Link>,
    },
    (isManager || isSeniorOperator) && {
      key: '/holidays',
      label: <Link to="/holidays">Country holidays</Link>,
    },
  ].filter(Boolean) as MenuProps['items'];

  return (
    <Menu
      theme="dark"
      mode="horizontal"
      defaultSelectedKeys={[window.location.pathname]}
      style={{ marginRight: 'auto', flexGrow: 1 }}
      items={menuItems}
    />
  );
};

const AppRoutes: React.FC<{
  isObserver: boolean;
  isManager: boolean;
  isSeniorOperator: boolean;
}> = ({ isObserver, isManager, isSeniorOperator }) => {
  const isChile = false; // TODO: implement proper country detection

  return (
    <Routes>
      {!isObserver && <Route path="/" element={<OrderPageViewNew />} />}
      {!isObserver && <Route path="/tracking" element={<TrackingPageView />} />}
      {isManager && <Route path="/users" element={<UserEditPage />} />}
      {(isManager || isSeniorOperator || !isChile) && (
        <Route path="/districts" element={<DistrictsPageView />} />
      )}
      {(isManager || isSeniorOperator) && (
        <Route path="/districts2" element={<DistrictsPageViewNew />} />
      )}
      {(isManager || isSeniorOperator) && (
        <Route path="/holidays" element={<CountryHolidaysPageView />} />
      )}
      {isObserver && <Route path="/" element={<TrackingPageView />} />}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const { Header } = Layout;

export const DeliveryAdmin: React.FC = () => {
  const { isManager, isSeniorOperator, isObserver } = useUserRoles();
  const { permissionStatus, requestPermission, isInitializing } = useFCM();

  return (
    <ExpressOrdersProvider>
      <Layout style={{ height: '100%' }}>
        <Header
          style={{
            height: '32px',
            lineHeight: '32px',
            padding: '0 8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <NavigationMenu
            isObserver={isObserver}
            isManager={isManager}
            isSeniorOperator={isSeniorOperator}
          />
          <NotificationStatusIndicator
            permissionStatus={permissionStatus}
            onEnableClick={requestPermission}
            isLoading={isInitializing}
          />
          <UTCOffsets />
          <div style={{ color: 'white', marginRight: 14 }}>
            v. {import.meta.env.VITE_BUILD_VERSION || 'dev'}
          </div>
          <LogoutButtonView />
          <RealtimeNotify />
          <HeaderSelect />
        </Header>
        <AppRoutes
          isObserver={isObserver}
          isManager={isManager}
          isSeniorOperator={isSeniorOperator}
        />
      </Layout>
    </ExpressOrdersProvider>
  );
};
